import { v4 as uuidv4 } from "uuid";
import {
  getCaSettingByKey,
  getCaType,
} from "../../../../../builders/settings/CaSetting";
import {
  findOrCreateEntry,
  getCommaOut,
} from "../../../../../utils/hooks/dataHandler";
import {
  CA_ADDITION_SOURCE_KEYS,
  FA_REGISTER_CURRENT_YEAR_SOURCE,
  FA_REGISTER_PRIOR_YEAR_SOURCE,
} from "../../../../../utils/static/CASchedule";

import {
  applyNetDisposalConsideration,
  hasNetDisposalConsideration,
  isNetDisposalEnabled,
} from "./s19dNetDisposal";

const DEFAULT_ADDITION_SOURCE_KEY = CA_ADDITION_SOURCE_KEYS.manual;

const toS19DNumber = (value) => {
  const number = Number(getCommaOut(value));
  return Number.isFinite(number) ? number : 0;
};

const applyS19DDefaults = (row, caType) => {
  if (caType !== "S19D") return row;

  return {
    ...row,
    cost: row.cost ?? 0,
    assetInUseAtFYE: row.assetInUseAtFYE ?? true,
  };
};

const getHirePurchaseAssetCost = (assets = []) =>
  (Array.isArray(assets) ? assets : []).reduce(
    (total, asset) => total + toS19DNumber(asset?.cost),
    0
  );

const normalizeAdditionSourceKey = (sourceKey) =>
  sourceKey === CA_ADDITION_SOURCE_KEYS.plSchedules
    ? DEFAULT_ADDITION_SOURCE_KEY
    : sourceKey || DEFAULT_ADDITION_SOURCE_KEY;

const isFARegisterPriorYearRow = (row) =>
  row?.source?.type === FA_REGISTER_PRIOR_YEAR_SOURCE || row?.faRegisterSource;

const isFARegisterSourceValue = (sourceInfo) =>
  sourceInfo === "FA Register" ||
  sourceInfo?.type === FA_REGISTER_CURRENT_YEAR_SOURCE ||
  sourceInfo?.source === "FA Register" ||
  sourceInfo?.source?.source === "FA Register";

const getAdditionSourceKey = (row) => {
  if (row?.sourceKey) return normalizeAdditionSourceKey(row.sourceKey);

  if (
    isFARegisterSourceValue(row?.source) ||
    row?.faRegisterSource ||
    row?.isImportedFromFARegister
  ) {
    return CA_ADDITION_SOURCE_KEYS.faRegister;
  }

  return DEFAULT_ADDITION_SOURCE_KEY;
};

const getAdditionRowKey = (row) =>
  [
    row?.caName,
    row?.isDeferred ? "deferred" : "nonDeferred",
    row?.noOfYearClaims,
    getAdditionSourceKey(row),
  ].join("_");

const buildAdditionRowKey = (caName, type, noOfYearClaims, sourceKey) =>
  [caName, type, noOfYearClaims, normalizeAdditionSourceKey(sourceKey)].join(
    "_"
  );

const mergeAdditionSourceValues = (target, source = {}) => {
  target.deferred += Number(source.deferred) || 0;
  target.nonDeferred += Number(source.nonDeferred) || 0;

  if (source.sources && typeof source.sources === "object") {
    target.sources = {
      ...(target.sources || {}),
      ...source.sources,
    };
  }

  return target;
};

const getAdditionSourceGroups = (values = {}) => {
  if (values?.sourceGroups && typeof values.sourceGroups === "object") {
    const sourceGroupMap = new Map();

    Object.entries(values.sourceGroups)
      .filter(
        ([, sourceValues]) => sourceValues && typeof sourceValues === "object"
      )
      .forEach(([sourceKey, sourceValues]) => {
        const normalizedSourceKey = normalizeAdditionSourceKey(
          sourceValues.sourceKey || sourceKey
        );
        const existing = sourceGroupMap.get(normalizedSourceKey) || {
          deferred: 0,
          nonDeferred: 0,
          sourceKey: normalizedSourceKey,
          sourceLabel: "",
        };

        sourceGroupMap.set(
          normalizedSourceKey,
          mergeAdditionSourceValues(existing, sourceValues)
        );
      });

    const groups = Array.from(sourceGroupMap, ([sourceKey, sourceValues]) => ({
      sourceKey,
      values: sourceValues,
    }));

    if (groups.length > 0) return groups;
  }

  return [{ sourceKey: DEFAULT_ADDITION_SOURCE_KEY, values }];
};

const getCurrentYearDisposalSourceKey = (sourceGroups = []) => {
  const nonDeferredGroups = sourceGroups.filter(
    ({ values }) => Number(values?.nonDeferred) || 0
  );

  if (nonDeferredGroups.length === 0) return null;

  return (
    nonDeferredGroups.find(
      ({ sourceKey }) => sourceKey === DEFAULT_ADDITION_SOURCE_KEY
    ) || nonDeferredGroups[0]
  ).sourceKey;
};

const getSummarySourceInfo = (
  values = {},
  type,
  sourceKey = DEFAULT_ADDITION_SOURCE_KEY
) => {
  const typeSourceInfo = values.sources?.[type] || {};
  const sourceInfo = values.source || typeSourceInfo;

  return {
    sourceKey,
    faRegisterSource:
      sourceKey === CA_ADDITION_SOURCE_KEYS.faRegister ||
      values.faRegisterSource ||
      values.isImportedFromFARegister ||
      typeSourceInfo.faRegisterSource ||
      typeSourceInfo.isImportedFromFARegister ||
      isFARegisterSourceValue(sourceInfo) ||
      isFARegisterSourceValue(typeSourceInfo),
  };
};

const applySummarySourceInfo = (entry, sourceInfo) => {
  entry.sourceKey = sourceInfo.sourceKey || DEFAULT_ADDITION_SOURCE_KEY;

  if (sourceInfo.faRegisterSource) {
    entry.faRegisterSource = true;
    entry.isImportedFromFARegister = true;
    entry.source = {
      type: FA_REGISTER_CURRENT_YEAR_SOURCE,
      source: "FA Register",
    };
    return;
  }

  if (entry.source?.type === FA_REGISTER_CURRENT_YEAR_SOURCE) {
    delete entry.source;
  }
  delete entry.faRegisterSource;
  delete entry.isImportedFromFARegister;
};

export const defineAAbreakdown = (row, caSetting) => {
  if (!caSetting?.straightLineMethod) {
    row.breakdown =
      caSetting?.AAbreakdown?.length <= 0 ? [] : caSetting?.AAbreakdown;
  }
  return row;
};

const calculateNoOfYearClaimsCf = (row, caSetting) =>
  row.isDeferred && caSetting?.caType !== "S19D"
    ? row.noOfYearClaimsBf
    : Math.max(0, (Number(row.noOfYearClaimsBf) || 0) - 1);

export const calculateAllowance = (row, caSetting, yearOfAssessment) => {
  const deferredType = row.isDeferred ? "deferred" : "nonDeferred";
  const isS19D = caSetting?.caType === "S19D";
  if (isS19D) row.cost = toS19DNumber(row.cost);
  const disposalAdjustment = isS19D
    ? applyNetDisposalConsideration(row, yearOfAssessment)
    : null;
  const claimYears = disposalAdjustment?.years ?? Number(row.noOfYearClaims);
  row.noOfYearClaimsCf = calculateNoOfYearClaimsCf(row, caSetting);

  if (caSetting?.allowanceType?.IA) {
    if (!row.initialAllowanceRate) {
      row.initialAllowanceRate = caSetting.IA;
    }

    if (row.ya !== yearOfAssessment) {
      row.initialAllowanceRate = 0;
    }

    row.initialAllowance = calculateInitialAllowance(
      row.totalAmount,
      row.ya,
      deferredType,
      row.caName,
      row.noOfYearClaimsBf,
      row.initialAllowanceRate,
      yearOfAssessment
    );
  } else row.initialAllowance = 0;

  row = defineAAbreakdown(row, caSetting);

  row.annualAllowance =
    isS19D &&
    (row.assetInUseAtFYE === false ||
      !Number.isFinite(claimYears) ||
      claimYears <= 0 ||
      !(Number(row.noOfYearClaimsBf) > 0))
      ? 0
      : calculateAnnualAllowance(
          isS19D
            ? (disposalAdjustment?.amount ?? (Number(row.cost) || 0))
            : Math.round(row.totalAmount) + Math.round(row.initialAllowance),
          deferredType,
          row.caName,
          isS19D ? claimYears : row.noOfYearClaimsBf,
          caSetting,
          row.breakdown
        );

  if (caSetting?.baseAllowanceCap && row.annualAllowance !== "Deferred") {
    row.annualAllowance = Math.min(row.annualAllowance, caSetting.cap);
  }

  if (isS19D && typeof row.annualAllowance === "number") {
    const assets = row.source?.assets;
    // Register rows are rounded per asset; keep the grouped CA row on that basis.
    if (
      row.annualAllowance < 0 &&
      !disposalAdjustment &&
      Array.isArray(assets) &&
      assets.length > 0 &&
      Math.abs(
        assets.reduce((sum, asset) => sum + toS19DNumber(asset.cost), 0) -
          row.cost
      ) < 0.00001
    ) {
      row.annualAllowance = -assets.reduce(
        (sum, asset) =>
          sum +
          Math.min(
            Math.round(toS19DNumber(asset.cost) / claimYears),
            Math.max(0, Math.round(toS19DNumber(asset.twdvBf)))
          ),
        0
      );
    }
    // Do not claim more than the remaining balance, including final-year rounding.
    const claimed = Math.min(
      Math.abs(row.annualAllowance),
      Math.max(0, Math.round(toS19DNumber(row.totalAmount)))
    );
    row.annualAllowance = claimed === 0 ? 0 : -claimed;
  }

  return row;
};

export const calculateInitialAllowance = (
  amount,
  year,
  type,
  caName,
  noOfYear,
  rate,
  yearOfAssessment
) => {
  if (type === "deferred") return "Deferred";
  if (!caName || yearOfAssessment !== year || noOfYear === 0) return 0;

  return -Math.abs((amount * rate) / 100);
};

export const calculateAnnualAllowance = (
  amount,
  type,
  caName,
  noOfYear,
  caSetting,
  breakdown
) => {
  if (type === "deferred") return "Deferred";
  if (!caName) return 0;

  let annualAllowance = amount;
  if (noOfYear > 1) annualAllowance = amount / noOfYear;

  if (caSetting?.straightLineMethod && !caSetting?.allowanceType?.IA)
    return amount < 0
      ? Math.abs(Math.round(annualAllowance))
      : -Math.abs(Math.round(annualAllowance));

  if (noOfYear <= 1) {
    return amount < 0
      ? Math.abs(Math.round(amount))
      : -Math.abs(Math.round(amount));
  }
  let caBreakdown = caSetting?.AAbreakdown;

  if (!caBreakdown || caBreakdown.length <= 0) {
    if (breakdown && breakdown.length > 0) {
      return -Math.abs(Math.round(amount * (breakdown[noOfYear - 1] / 100)));
    } else {
      return -Math.abs(Math.round(amount / noOfYear));
    }
  } else {
    return -Math.abs(
      Math.round(amount * (caSetting.AAbreakdown[noOfYear - 1] / 100))
    );
  }
};

export const calculateTwdvCf = (
  totalAmount,
  annualAllowance,
  isDeferred,
  initialAllowance
) => {
  if (isDeferred) return totalAmount;

  let total = Math.round(totalAmount) + Math.round(annualAllowance);
  if (initialAllowance) return total + Math.round(initialAllowance);
  return total;
};

export const calculateEnhancedAmount = (amount, caSetting) => {
  return (
    Math.min(Number(getCommaOut(caSetting.furtherAllowanceCap)), amount) *
    (Number(getCommaOut(caSetting.enhancedAllowancePercentage)) / 100)
  );
};

export const calculateEnhancedSet = (
  row,
  scheduleDetails,
  skipUpdateCurrentDisposalEnhanced = false
) => {
  if (scheduleDetails?.enhancedAllowance && scheduleDetails?.isOnlyEnhanced) {
    row.allowanceType = "Enhanced";
  }

  if (scheduleDetails?.enhancedAllowance && row.allowanceType === "Enhanced") {
    if (!row.enhanced.ca_status || row.enhanced.ca_status !== "manual") {
      row.enhanced.currentAmount = calculateEnhancedAmount(
        row.currentAmount,
        scheduleDetails
      );
    }

    if (!skipUpdateCurrentDisposalEnhanced) {
      if (row.enhanced.cyd_status !== "manual") {
        row.enhanced.cyd_status = "autoload";
        row.enhanced.currentYearDisposal = calculateEnhancedAmount(
          row.currentYearDisposal,
          scheduleDetails
        );
      }
    }
    row.enhanced.totalAmount =
      (row.enhanced.twdvBf || 0) + row.enhanced.currentAmount;

    if (row.enhanced.currentYearDisposal) {
      row.enhanced.totalAmount += row.enhanced.currentYearDisposal;
    }

    row.enhanced.annualAllowance = calculateAnnualAllowance(
      Math.round(row.enhanced.totalAmount) +
        Math.round(row.enhanced.initialAllowance || 0),
      row.isDeferred ? "deferred" : "nonDeferred",
      row.caName,
      row.noOfYearClaimsBf,
      scheduleDetails,
      row.breakdown
    );

    row.enhanced.twdvCf = calculateTwdvCf(
      row.enhanced.totalAmount,
      row.enhanced.annualAllowance,
      row.isDeferred
    );
  } else {
    row.enhanced = {};
    row.enhanced.twdvBf = 0;
    row.enhanced.currentAmount = 0;
    row.enhanced.currentYearDisposal = 0;
    row.enhanced.annualAllowance = 0;
    row.enhanced.twdvCf = 0;
    row.enhanced.totalAmount = 0;
  }

  return row;
};

const calculateDisregardAmount = (row, scheduleDetails) => {
  if (scheduleDetails?.caType === "S16" && row.isDisregard) {
    if (!row.disregard)
      row.disregard = {
        initialAllowance: 0,
        annualAllowance: 0,
      };

    row.disregard.initialAllowance =
      row.initialAllowance * (row.disregardRate / 100);
    row.disregard.annualAllowance =
      row.annualAllowance * (row.disregardRate / 100);
  } else {
    if (row.disregard) {
      row.disregard.initialAllowance = 0;
      row.disregard.annualAllowance = 0;
    }
  }

  return row;
};

const processEntry = (entry, scheduleDetails, ya) => {
  if (!entry.isDisregard) entry.disregardRate = 0;

  entry = calculateAllowance(entry, scheduleDetails, ya);
  entry.twdvCf = calculateTwdvCf(
    entry.totalAmount,
    entry.annualAllowance,
    entry.isDeferred,
    entry.initialAllowance
  );
  entry = calculateEnhancedSet(entry, scheduleDetails);
  entry = calculateDisregardAmount(entry, scheduleDetails);

  return entry;
};

export const generateAdditions = (
  details,
  summary,
  twdvDetails,
  caSettings,
  caType,
  yearOfAssessment,
  faRegisterRows = []
) => {
  const existingAdditions = new Map(
    (details || []).map((a) => [getAdditionRowKey(a), a])
  );

  Object.entries(summary || {}).forEach(([caName, yearGroups]) => {
    if (!caName) return;

    const scheduleDetails = getCaSettingByKey(caSettings, caName);

    Object.entries(yearGroups || {}).forEach(([yearStr, values]) => {
      const noOfYearClaims = parseInt(yearStr, 10);
      if (!Number.isFinite(noOfYearClaims)) return;

      const sourceGroups = getAdditionSourceGroups(values);
      const currentYearDisposalSourceKey =
        getCurrentYearDisposalSourceKey(sourceGroups);

      sourceGroups.forEach(({ sourceKey, values: sourceValues }) => {
        ["deferred", "nonDeferred"].forEach((type) => {
          const amount = Number(sourceValues[type]) || 0;
          if (amount === 0) return;

          const key = buildAdditionRowKey(
            caName,
            type,
            noOfYearClaims,
            sourceKey
          );
          let entry = findOrCreateEntry(existingAdditions, key, () => ({
            ya: yearOfAssessment,
            caName,
            caSummary: scheduleDetails?.caSummary,
            allowanceType: "Base",
            noOfYearClaims,
            noOfYearClaimsBf: noOfYearClaims,
            twdvBf: 0,
            ...(scheduleDetails?.caType === "S19D"
              ? { cost: 0, assetInUseAtFYE: true }
              : {}),
            isDeferred: type === "deferred",
            isDisregard: 0,
            disregardRate: 0,
            initialAllowance: 0,
            initialAllowanceRate: 0,
            annualAllowance: 0,
            twdvCf: 0,
            noOfYearClaimsCf: noOfYearClaims - 1,
            enhanced: {},
            disregard: {},
          }));

          if (!entry) return;

          applySummarySourceInfo(
            entry,
            getSummarySourceInfo(sourceValues, type, sourceKey)
          );

          if (scheduleDetails?.caType === "S19D") {
            // FA Additions summary values are the source cost for current-year S19D rows.
            entry.cost = amount;
            entry.assetInUseAtFYE = entry.assetInUseAtFYE ?? true;
            if (sourceKey === CA_ADDITION_SOURCE_KEYS.faRegister) {
              entry.source.assets = faRegisterRows
                .filter(
                  (asset) =>
                    asset.caType === caName &&
                    Number(asset.yaOfPurchase) === Number(yearOfAssessment) &&
                    Number(asset.noOfYearsClaim) === noOfYearClaims &&
                    Boolean(asset.deferred) === entry.isDeferred &&
                    !asset.hp
                )
                .map((asset) => ({
                  cost: toS19DNumber(asset.costOfAsset),
                  twdvBf: toS19DNumber(asset.costOfAsset),
                }));
            }
          }

          entry.currentAmount = amount;

          if (
            type === "nonDeferred" &&
            sourceKey === currentYearDisposalSourceKey
          ) {
            const summaryEntry = (twdvDetails || []).find(
              (summary) =>
                summary.yaOfPurchase == entry.ya &&
                summary.caName === entry.caName &&
                summary.noOfYearsBf === entry.noOfYearClaimsBf &&
                !summary.isHirePurchase
            );

            entry.currentYearDisposal = -(summaryEntry?.twdv || 0);
          } else entry.currentYearDisposal = 0;

          entry.totalAmount =
            (entry.twdvBf || 0) + amount + (entry.currentYearDisposal || 0);

          entry = processEntry(entry, scheduleDetails, yearOfAssessment);
        });
      });
    });
  });

  // Clean up rows that no longer have an autoload source.
  const validKeys = new Set(
    Object.entries(summary || {}).flatMap(([caName, yearGroups]) =>
      Object.entries(yearGroups || {}).flatMap(([yearStr, values]) => {
        const noOfYearClaims = parseInt(yearStr, 10);
        if (!Number.isFinite(noOfYearClaims)) return [];

        return getAdditionSourceGroups(values).flatMap(
          ({ sourceKey, values: sourceValues }) =>
            ["deferred", "nonDeferred"]
              .filter((type) => Number(sourceValues[type]) || 0)
              .map((type) =>
                buildAdditionRowKey(caName, type, noOfYearClaims, sourceKey)
              )
        );
      })
    )
  );

  for (const key of existingAdditions.keys()) {
    if (!validKeys.has(key)) {
      existingAdditions.delete(key);
    }
  }

  return Array.from(existingAdditions.values()).map((entry) =>
    applyS19DDefaults(entry, caType)
  );
};

function sumByFrom(data, year) {
  let total = 0;

  for (const [key, arr] of Object.entries(data)) {
    if (!Array.isArray(arr)) continue;

    for (const item of arr) {
      if (item.from === year) {
        total += item.amount;
      }
    }
  }

  return total;
}

function sumTwdv(data, year) {
  let total = 0;

  data.forEach((row) => {
    if (row.year === year) total += row.twdvBf;
  });

  return total;
}

function sumYearlyFrom(data, year) {
  const arr = data[year];
  if (!arr) return 0;

  return arr.reduce((sum, item) => sum + item.amount, 0);
}

function sumOnlyYear(data, year) {
  const arr = data[year];
  if (!arr) return 0;

  return arr
    .filter((item) => item.from === year)
    .reduce((sum, item) => sum + item.amount, 0);
}

function getHpCaName(row) {
  return row?.caName === "S16 - (LIA / IBA)" ? row.s16Type : row?.caName;
}

function getDisposalRowCaName(row) {
  return row?.caName === "S16 - (LIA / IBA)" ? row.s16Type : row?.caName;
}

function getDisposalRowKey(row) {
  return [
    isFARegisterPriorYearRow(row) ? "faRegister" : "normal",
    getDisposalRowCaName(row),
    row?.ya,
    row?.noOfYearClaims,
    row?.noOfYearClaimsBf,
    row?.isDeferred ? "deferred" : "nonDeferred",
    row?.caName === "S19D" && row?.s19dWdaBasis
      ? row.source?.rowKeys?.join("|") || row.rowId || ""
      : "",
  ].join("_");
}

function hpRowsMatch(savedRow, generatedRow) {
  const summariesEqual =
    Array.isArray(savedRow.caSummary) && Array.isArray(generatedRow.caSummary)
      ? JSON.stringify(savedRow.caSummary) ===
        JSON.stringify(generatedRow.caSummary) // deep compare arrays
      : savedRow.caSummary === generatedRow.caSummary; // primitive compare
  return (
    savedRow.ya === generatedRow.ya &&
    getHpCaName(savedRow) === getHpCaName(generatedRow) &&
    summariesEqual &&
    savedRow.noOfYearClaims === generatedRow.noOfYearClaims &&
    savedRow.noOfYearClaimsBf === generatedRow.noOfYearClaimsBf &&
    savedRow.isDeferred === generatedRow.isDeferred
  );
}

function findExistingHpRow(savedData, generatedRow) {
  return savedData?.find(
    (savedRow) => !savedRow.manualData && hpRowsMatch(savedRow, generatedRow)
  );
}

function getManualHpRows(savedData) {
  return savedData?.filter((savedRow) => savedRow.manualData) || [];
}

export const generateHirePurchaseRows = (
  hpPrevAdditionData,
  hpPrevDisposalData,
  hpRawData,
  caSettings,
  caType,
  yearOfAssessment
) => {
  if (caType === "S19D") return { additions: [], disposals: [] };

  const prepareManualRow = (row) => {
    const entry = applyS19DDefaults(row, caType);
    return caType === "S19D"
      ? processEntry(
          entry,
          getCaSettingByKey(caSettings, entry.caName),
          yearOfAssessment
        )
      : entry;
  };
  const manualAdditions =
    getManualHpRows(hpPrevAdditionData).map(prepareManualRow);
  const manualDisposals =
    getManualHpRows(hpPrevDisposalData).map(prepareManualRow);
  const additions = [];
  const disposals = [];

  if (!hpRawData || !hpRawData.summary) {
    additions.push(...manualAdditions);
    disposals.push(...manualDisposals);
    additions.sort((a, b) => a.ya - b.ya);
    disposals.sort((a, b) => a.ya - b.ya);
    return { additions, disposals };
  }

  for (const summaryType in hpRawData.summary) {
    const summaryCa = getCaType(caSettings, summaryType);
    const isS19 = summaryType === "S19";
    if (summaryCa === caType) {
      const scheduleDetails = getCaSettingByKey(caSettings, summaryType);
      for (const key in hpRawData.summary[summaryType]) {
        const entry = hpRawData.summary[summaryType][key];
        let baseData = {
          ya: entry.year,
          caName: summaryType,
          caSummary: scheduleDetails.caSummary,
          allowanceType: "Base",
          noOfYearClaims: entry.noOfYears,
          noOfYearClaimsBf: entry.noOfYearsBf,
          twdvBf: 0,
          ...(scheduleDetails?.caType === "S19D"
            ? {
                cost: getHirePurchaseAssetCost(entry.assets),
                assetInUseAtFYE: true,
              }
            : {}),
          isDeferred: entry.deferred,
          isDisregard: 0,
          disregardRate: 0,
          initialAllowance: 0,
          initialAllowanceRate: 0,
          annualAllowance: 0,
          twdvCf: 0,
          noOfYearClaimsCf: calculateNoOfYearClaimsCf(
            { isDeferred: entry.deferred, noOfYearClaimsBf: entry.noOfYearsBf },
            scheduleDetails
          ),
          currentYearDisposal: 0,
          principalPaid: 0,
          enhanced: {},
          disregard: {},
          isHirePurchase: true,
          hpAssets: entry.assets,
        };

        if (entry.year > yearOfAssessment) continue;

        const isCurrentYear = entry.year === yearOfAssessment;

        if (isCurrentYear) {
          let addition = {
            ...baseData,
            rowId: uuidv4(),
            currentAmount: entry.twdvBf,
            currentYearDisposal: entry.disposedAmount,
            principalPaid: entry.principalPaid,
          };

          if (manualAdditions.some((row) => hpRowsMatch(row, addition)))
            continue;

          const existing = findExistingHpRow(hpPrevAdditionData, addition);

          if (existing) {
            addition.enhanced = existing.enhanced;
            addition.isDisregard = existing.isDisregard;
            addition.disregardRate = existing.disregardRate;
            addition.initialAllowanceRate = existing.initialAllowanceRate;
            if (caType !== "S19D")
              addition.cost = existing.cost ?? addition.cost;
            addition.assetInUseAtFYE =
              existing.assetInUseAtFYE ?? addition.assetInUseAtFYE;
          }

          if (isS19) {
            addition.currentAmount = -(entry?.aa || 0);
          }

          addition.totalAmount =
            (addition.twdvBf || 0) +
            (addition.currentAmount || 0) +
            (addition.currentYearDisposal || 0);

          if (isS19) {
            addition.initialAllowanceRate = 0;
            addition.initialAllowance =
              entry?.disposed ? 0 : entry.ia || 0;
            addition.annualAllowance =
              addition?.isDeferred || entry?.disposed ? 0 : entry.aa || 0;
            addition.twdvCf = calculateTwdvCf(
              addition.totalAmount,
              addition.annualAllowance,
              addition.isDeferred,
              0
            );
            addition = calculateEnhancedSet(addition, scheduleDetails);
            addition = calculateDisregardAmount(addition, scheduleDetails);
          } else {
            addition = processEntry(
              addition,
              scheduleDetails,
              yearOfAssessment
            );
          }
          additions.push(addition);
        } else {
          let disposal = {
            ...baseData,
            rowId: uuidv4(),
            twdvBf: entry.twdvBf,
            ori_twdvBf: entry.twdvBf,
            currentAmount: entry.disposedAmount,
            principalPaid: entry.principalPaid,
          };

          if (manualDisposals.some((row) => hpRowsMatch(row, disposal)))
            continue;

          const existing = findExistingHpRow(hpPrevDisposalData, disposal);

          if (existing) {
            disposal.allowanceType = existing.allowanceType;
            disposal.rowId = existing.rowId;
            disposal.twdvBf = existing.twdvBf;
            disposal.isDisregard = existing.isDisregard;
            disposal.disregardRate = existing.disregardRate;
            if (caType !== "S19D")
              disposal.cost = existing.cost ?? disposal.cost;
            disposal.assetInUseAtFYE =
              existing.assetInUseAtFYE ?? disposal.assetInUseAtFYE;
            if (caType === "S19D") {
              disposal.netDisposalConsiderationEnabled =
                existing.netDisposalConsiderationEnabled;
              disposal.s19dWdaBasis = existing.s19dWdaBasis;
              disposal.s19dWdaClaimedBf = existing.s19dWdaClaimedBf;
              disposal.s19dBcChargedBf = existing.s19dBcChargedBf;
              if (hasNetDisposalConsideration(existing)) {
                disposal.netDisposalConsideration =
                  existing.netDisposalConsideration;
                disposal.s19dOriginalDisposalAmount = entry.disposedAmount || 0;
              }
            }
          }

          if (isS19) {
            disposal.twdvBf = -(entry.aa || 0);
            if (entry.currentYearDisposed) {
              disposal.currentAmount = 0;
            }
          }

          disposal.totalAmount =
            (disposal.twdvBf || 0) + (disposal.currentAmount || 0);

          if (isS19) {
            disposal.initialAllowanceRate = 0;
            disposal.initialAllowance = disposal?.isDeferred
              ? 0
              : entry.ia || 0;
            disposal.annualAllowance =
              disposal?.isDeferred || entry?.disposed ? 0 : entry.aa || 0;
            disposal.twdvCf = calculateTwdvCf(
              disposal.totalAmount,
              disposal.annualAllowance,
              disposal.isDeferred,
              0
            );
          } else {
            calculateAllowance(disposal, scheduleDetails, yearOfAssessment);
            disposal.twdvCf = calculateTwdvCf(
              disposal.totalAmount,
              disposal.annualAllowance,
              disposal.isDeferred,
              disposal.initialAllowance
            );
          }

          if (existing) {
            disposal.enhanced = existing.enhanced;
          } else
            disposal = calculateEnhancedSet({ ...disposal }, scheduleDetails);
          disposal = calculateDisregardAmount({ ...disposal }, scheduleDetails);

          disposals.push(disposal);
        }
      }
    }
  }

  additions.push(...manualAdditions);
  disposals.push(...manualDisposals);

  additions.sort((a, b) => a.ya - b.ya);
  disposals.sort((a, b) => a.ya - b.ya);

  return {
    additions: additions.map((row) => applyS19DDefaults(row, caType)),
    disposals: disposals.map((row) => applyS19DDefaults(row, caType)),
  };
};

export const generateDisposal = (
  details,
  summary,
  caSettings,
  caType,
  yearOfAssessment
) => {
  const existingDisposals = new Map(
    details.map((d) => [getDisposalRowKey(d), d])
  );

  existingDisposals.forEach((disposal) => {
    const disposalCaName = getDisposalRowCaName(disposal);
    const matchingEntry = summary.find(
      (e) =>
        e.caName === disposalCaName &&
        e.yaOfPurchase == disposal.ya &&
        e.noOfYearsBf == disposal.noOfYearClaimsBf
    );

    const scheduleDetails = getCaSettingByKey(caSettings, disposal.caName);

    if (scheduleDetails?.caType === "S19D") {
      // Preserve original cost; the disposal summary only covers disposed assets.
      disposal.cost = disposal.cost ?? 0;
      disposal.assetInUseAtFYE = disposal.assetInUseAtFYE ?? true;
      if (isNetDisposalEnabled(disposal))
        disposal.netDisposalConsideration = matchingEntry?.twdv ?? 0;
    }

    const twdv = matchingEntry?.twdv || 0;
    disposal.currentAmount = -twdv;
    if (hasNetDisposalConsideration(disposal))
      disposal.s19dOriginalDisposalAmount = disposal.currentAmount;
    disposal.totalAmount = disposal.twdvBf - twdv;

    if (!disposal.isDisregard) {
      disposal.disregardRate = 0;
    }

    disposal = calculateAllowance(disposal, scheduleDetails, yearOfAssessment);

    disposal.twdvCf = calculateTwdvCf(
      disposal.totalAmount,
      disposal.annualAllowance,
      disposal.isDeferred,
      disposal.initialAllowance
    );

    disposal = calculateEnhancedSet(disposal, scheduleDetails);

    disposal = calculateDisregardAmount(disposal, scheduleDetails);
  });

  return Array.from(existingDisposals.values()).map((row) =>
    applyS19DDefaults(row, caType)
  );
};

export const calculateTotal = (details, isHirePurchase = false) => {
  const safeAdd = (a, b) =>
    isNaN(b) ? Math.round(a) : Math.round(a) + Math.round(b);
  const isDeferred = (val) => (val === "Deferred" ? 0 : val || 0);

  const totals = {
    twdvBf: 0,
    cost: 0,
    netDisposalConsideration: 0,
    currentAmount: 0,
    currentYearDisposal: 0,
    totalAmount: 0,
    initialAllowance: 0,
    annualAllowance: 0,
    twdvCf: 0,
    disregard: {
      initialAllowance: 0,
      annualAllowance: 0,
    },
    ...(isHirePurchase && { principalPaid: 0 }),
  };

  const accumulate = (item) => {
    if (!item) return;

    totals.twdvBf = safeAdd(totals.twdvBf, item.twdvBf);
    totals.cost = safeAdd(totals.cost, item.cost);
    totals.netDisposalConsideration = safeAdd(
      totals.netDisposalConsideration,
      isNetDisposalEnabled(item) ? item.netDisposalConsideration : 0
    );
    totals.currentAmount = safeAdd(totals.currentAmount, item.currentAmount);
    totals.currentYearDisposal = safeAdd(
      totals.currentYearDisposal,
      item.currentYearDisposal
    );
    totals.totalAmount = safeAdd(totals.totalAmount, item.totalAmount);
    totals.initialAllowance = safeAdd(
      totals.initialAllowance,
      isDeferred(item.initialAllowance)
    );
    totals.annualAllowance = safeAdd(
      totals.annualAllowance,
      isDeferred(item.annualAllowance)
    );
    totals.twdvCf = safeAdd(totals.twdvCf, item.twdvCf);

    totals.disregard.initialAllowance = safeAdd(
      totals.disregard.initialAllowance,
      Math.round(item.disregard?.initialAllowance) || 0
    );
    totals.disregard.annualAllowance = safeAdd(
      totals.disregard.annualAllowance,
      Math.round(item.disregard?.annualAllowance) || 0
    );

    if (isHirePurchase) {
      totals.principalPaid = safeAdd(totals.principalPaid, item.principalPaid);
    }

    accumulate(item.enhanced);
  };

  if (details && details.length > 0)
    details.forEach((item) => {
      accumulate(item);
    });

  return totals;
};

export const updateRowValues = (
  row,
  caSettings,
  yearOfAssessment,
  skipUpdateCurrentDisposalEnhanced = false
) => {
  const scheduleDetails = getCaSettingByKey(caSettings, row.caName);

  row = calculateAllowance(row, scheduleDetails, yearOfAssessment);

  row = calculateEnhancedSet(
    row,
    scheduleDetails,
    skipUpdateCurrentDisposalEnhanced
  );

  row = calculateDisregardAmount(row, scheduleDetails);

  row.twdvCf = calculateTwdvCf(
    row.totalAmount,
    row.annualAllowance,
    row.isDeferred,
    row.initialAllowance
  );
  return row;
};

export const populateTwdvToDisposal = (
  row,
  twdvDetails,
  caSettings,
  yearOfAssessment
) => {
  const scheduleDetails = getCaSettingByKey(caSettings, row.caName);
  const summaryEntry =
    row.caName === "S16 - (LIA / IBA)"
      ? twdvDetails.find(
          (summary) =>
            summary.yaOfPurchase == row.ya &&
            summary.caName === row.s16Type &&
            summary.noOfYearsBf === row.noOfYearClaimsBf
        )
      : twdvDetails.find(
          (summary) =>
            summary.yaOfPurchase == row.ya &&
            summary.caName === row.caName &&
            (scheduleDetails?.caType === "S19D"
              ? Number(summary.noOfYearsBf) === Number(row.noOfYearClaimsBf)
              : summary.noOfYearsBf === row.noOfYearClaimsBf)
        );

  row.currentAmount = summaryEntry ? -summaryEntry.twdv : 0;
  if (hasNetDisposalConsideration(row))
    row.s19dOriginalDisposalAmount = row.currentAmount;
  row.totalAmount = row.twdvBf - (summaryEntry?.twdv || 0);

  if (scheduleDetails?.caType === "S19D" && isNetDisposalEnabled(row))
    row.netDisposalConsideration = summaryEntry?.twdv ?? 0;

  row = calculateAllowance(row, scheduleDetails, yearOfAssessment);

  row.twdvCf = calculateTwdvCf(
    row.totalAmount,
    row.annualAllowance,
    row.isDeferred
  );

  return row;
};

export function handleCaNameField(
  row,
  value,
  twdvDetails,
  caSettings,
  yearOfAssessment,
  isHirePurchase = false,
  skipResetBf = false
) {
  row.caName = value;
  row.allowanceType = "Base";
  row.enhanced = {};

  let caDetails = getCaSettingByKey(caSettings, value);
  row.caSummary = caDetails.caSummary;
  if (caDetails.numberOfYears && caDetails.numberOfYears !== null) {
    row.noOfYearClaims = caDetails.numberOfYears || 0;
    if (!skipResetBf) row.noOfYearClaimsBf = caDetails.numberOfYears || 0;
  }
  row.noOfYearClaimsCf = calculateNoOfYearClaimsCf(row, caDetails);

  row.breakdown = [];
  if (!isHirePurchase && row.ya !== "" && twdvDetails !== null)
    row = populateTwdvToDisposal(
      row,
      twdvDetails,
      caSettings,
      yearOfAssessment
    );
  return row;
}

export const inputHandlerSwitch = (
  field,
  value,
  row,
  twdvDetails,
  caSettings,
  yearOfAssessment,
  allowanceType,
  isHirePurchase = false
) => {
  if (
    [
      "noOfYearClaims",
      "noOfYearClaimsBf",
      "cost",
      "twdvBf",
      "currentYearDisposal",
      "currentAmount",
      "principalPaid",
    ].includes(field)
  ) {
    value = Number(getCommaOut(value));
  }

  switch (field) {
    case "netDisposalConsiderationEnabled":
      row.netDisposalConsiderationEnabled = Boolean(value);
      if (value) {
        row = populateTwdvToDisposal(
          row,
          twdvDetails || [],
          caSettings,
          yearOfAssessment
        );
      }
      if (!value) {
        row.currentAmount = row.s19dOriginalDisposalAmount ?? row.currentAmount;
        row.totalAmount =
          toS19DNumber(row.twdvBf) + toS19DNumber(row.currentAmount);
        delete row.s19dOriginalDisposalAmount;
        delete row.s19dDisposalAdjustment;
      }
      break;
    case "netDisposalConsideration":
      row.netDisposalConsideration =
        value === null || String(value).trim() === ""
          ? null
          : Number(getCommaOut(value));
      if (!hasNetDisposalConsideration(row)) {
        row.currentAmount = row.s19dOriginalDisposalAmount ?? row.currentAmount;
        row.totalAmount =
          toS19DNumber(row.twdvBf) + toS19DNumber(row.currentAmount);
        delete row.s19dOriginalDisposalAmount;
        delete row.s19dDisposalAdjustment;
      }
      break;
    case "ya":
      row.ya = value;

      if (!isHirePurchase && row.caName !== "" && twdvDetails !== null)
        row = populateTwdvToDisposal(
          row,
          twdvDetails,
          caSettings,
          yearOfAssessment
        );
      break;
    case "caName":
      row = handleCaNameField(
        row,
        value,
        twdvDetails,
        caSettings,
        yearOfAssessment,
        isHirePurchase
      );
      break;
    case "isDeferred":
      row.isDeferred = value;
      break;
    case "assetInUseAtFYE":
      row.assetInUseAtFYE = Boolean(value);
      break;
    case "cost":
      row.cost = value;
      break;
    case "noOfYearClaimsBf":
      row.noOfYearClaimsBf = value;
      row.breakdown = [];
      if (!isHirePurchase && row.caName !== "" && twdvDetails !== null)
        row = populateTwdvToDisposal(
          row,
          twdvDetails,
          caSettings,
          yearOfAssessment
        );
      break;
    case "twdvBf":
      if (allowanceType === "Base") {
        row.twdvBf = value;
        row.totalAmount = row.twdvBf + row.currentAmount;
      } else if (allowanceType === "Enhanced") {
        row.enhanced.twdvBf = value;
        row.enhanced.totalAmount =
          row.enhanced.twdvBf + row.enhanced.currentAmount;
      }

      break;
    case "allowanceType":
      row.allowanceType = value;

      if (value === "Base") {
        row.enhanced = {};
      }
      break;
    case "noOfYearClaims":
      row.noOfYearClaims = value;
      row.noOfYearClaimsBf = value;
      row.breakdown = [];
      if (
        !isHirePurchase &&
        row.ya !== "" &&
        row.caName !== "" &&
        twdvDetails !== null
      )
        row = populateTwdvToDisposal(
          row,
          twdvDetails,
          caSettings,
          yearOfAssessment
        );
      break;
    case "isDisregard":
      row.isDisregard = value;

      if (!value) row.disregardRate = 0;

      row = calculateDisregardAmount(
        row,
        getCaSettingByKey(caSettings, row.caName)
      );

      break;
    case "disregardRate":
      row.disregardRate = value;

      row = calculateDisregardAmount(row, row.caName);

      break;
    case "initialAllowanceRate":
      row.initialAllowanceRate = value;
      break;
    case "s16Type":
      row.s16Type = value;
      break;
    case "principalPaid":
      row.principalPaid = value;

      break;
    case "currentYearDisposal":
    case "currentAmount":
      if (allowanceType === "Enhanced") {
        row.enhanced[field] = value;
        if (field === "currentYearDisposal") row.enhanced.cyd_status = "manual";
        else row.enhanced.ca_status = "manual";
      }
      break;
    default:
      return;
  }

  row.noOfYearClaimsCf = calculateNoOfYearClaimsCf(
    row,
    getCaSettingByKey(caSettings, row.caName)
  );
  return row;
};
